/**
 * New Deal dialog — originates a loan tranche (or several, one per
 * investor) directly in loan_book / risk_participation. Lives behind the
 * Loans page's existing "+New Deal" button.
 *
 * Three allocation modes, matching what the legacy Deal Room dialog
 * offered:
 *   - Single investor    — one risk participant takes the whole amount.
 *   - Pro-rata            — auto-split across all investors with available
 *                            Investec call-account cash, proportionally.
 *   - Manual              — hand-pick investors and amounts (must sum to
 *                            the total).
 *
 * Two-step flow: fill the form, click "Review" to see exactly what will
 * be written to the loan book (one row per tranche, including the admin
 * fee tranche if any), then a separate "Submit" commits it.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '../ui/table';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle2, AlertTriangle, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => (v ?? 0).toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });

// client.drawdown_fee_pct is a decimal fraction (e.g. 0.02 for 2%) of the
// new loan's principal (the loan amount, not Gross Purchase Price). Returns
// null (leave admin_fee alone) when the client has no fee % or no amount
// is entered yet, rather than clobbering a manually-entered fee with 0.
// Admin fee is entered as a % of the loan amount (2026-10-14); the amount is
// calculated from it unless the admin overrides the amount by hand.
const feeFromPct = (pctStr, loanAmountStr) => {
  const pct = parseFloat(pctStr);
  const amt = parseFloat(loanAmountStr);
  if (!pct || !amt) return '';
  return (amt * pct / 100).toFixed(2);
};

const computeDrawdownFee = (client, loanAmountStr) => {
  const amt = parseFloat(loanAmountStr);
  if (!client?.drawdown_fee_pct || !amt) return null;
  return (amt * client.drawdown_fee_pct).toFixed(2);
};

const COUNTRIES = [
  'South Africa', 'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia',
  'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus',
  'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil',
  'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde',
  'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo',
  'Congo, the Democratic Republic of the', 'Costa Rica', "Côte d'Ivoire", 'Croatia', 'Cuba', 'Cyprus',
  'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt',
  'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland',
  'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala',
  'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India',
  'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan',
  'Kenya', 'Kiribati', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia',
  'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives',
  'Mali', 'Malta', 'Mauritania', 'Mauritius', 'Mexico', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro',
  'Morocco', 'Mozambique', 'Myanmar', 'Namibia', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua',
  'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania',
  'Russia', 'Rwanda', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Somalia', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan',
  'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo',
  'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Uganda', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu',
  'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

const DEAL_TYPES = ['SRA', 'AOD', 'PEN', 'REBALANCE', 'TLA', 'FEE'];
const FEE_RP = '05-00'; // a FEE loan is booked to the fund's own RP, like an admin-fee tranche

const emptyForm = {
  client_id: '', loan_amount: '', interest_rate_pct: '', admin_fee: '', admin_fee_pct: '', admin_fee_overridden: false, ltv_pct: '100',
  purchase_date: new Date().toISOString().split('T')[0], repurchase_date: '',
  deal_type: 'SRA', seller: '', supplier: '', customer: '', category: '',
  country_of_origin: 'South Africa', destination_country: 'South Africa',
  loan_number: '', include_api: true, same_day_recycle: false,
};

export default function NewDealDialog({ open, onOpenChange, clients = [], onCreated }) {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [step, setStep] = useState('form'); // 'form' | 'review'
  const [mode, setMode] = useState('single'); // 'single' | 'pro_rata' | 'manual'
  const [form, setForm] = useState(emptyForm);
  const [singleRp, setSingleRp] = useState('');
  const [singleRpRatePct, setSingleRpRatePct] = useState('');
  const [manualRows, setManualRows] = useState([{ risk_participant: '', amount: '', rp_rate: '' }]);

  const [formOptions, setFormOptions] = useState({
    risk_participants: [], deal_types: DEAL_TYPES, categories: [], sellers: [], suppliers: [], customers: [],
  });
  const [availableCash, setAvailableCash] = useState([]);
  const [proRataAllocations, setProRataAllocations] = useState([]);
  const [loadingProRata, setLoadingProRata] = useState(false);
  const [ratePreview, setRatePreview] = useState(null);
  const [loadingRatePreview, setLoadingRatePreview] = useState(false);

  const [reviewRows, setReviewRows] = useState([]);
  const [buildingReview, setBuildingReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const resetAll = () => {
    setStep('form'); setMode('single'); setForm(emptyForm);
    setSingleRp(''); setSingleRpRatePct('');
    setManualRows([{ risk_participant: '', amount: '', rp_rate: '' }]);
    setProRataAllocations([]); setRatePreview(null);
    setReviewRows([]); setLastResult(null);
  };

  useEffect(() => {
    if (!open) return;
    resetAll();
    (async () => {
      try {
        const [opts, cash] = await Promise.all([
          axios.get(`${API_URL}/api/lms/new-deals/form-options`, { headers }),
          axios.get(`${API_URL}/api/lms/new-deals/available-cash`, { headers }),
        ]);
        setFormOptions(opts.data);
        setAvailableCash(cash.data.investors || []);
      } catch (err) {
        toast.error('Failed to load new deal form options');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Single-mode rate preview
  useEffect(() => {
    if (mode !== 'single' || singleRpRatePct || !singleRp || !form.interest_rate_pct) {
      if (mode === 'single') setRatePreview(null);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setLoadingRatePreview(true);
        const r = await axios.get(`${API_URL}/api/lms/new-deals/rate-preview`, {
          headers, params: { risk_participant: singleRp, interest_rate: parseFloat(form.interest_rate_pct) / 100 },
        });
        setRatePreview(r.data);
      } catch { setRatePreview(null); } finally { setLoadingRatePreview(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [mode, singleRp, singleRpRatePct, form.interest_rate_pct, headers]);

  // Pro-rata live split preview
  useEffect(() => {
    if (mode !== 'pro_rata' || !form.loan_amount || parseFloat(form.loan_amount) <= 0) {
      setProRataAllocations([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setLoadingProRata(true);
        const r = await axios.post(`${API_URL}/api/lms/new-deals/pro-rata-preview`,
          { total_amount: parseFloat(form.loan_amount) }, { headers });
        setProRataAllocations(r.data.allocations || []);
      } catch (err) {
        setProRataAllocations([]);
        toast.error(err.response?.data?.detail || 'Failed to compute pro-rata split');
      } finally { setLoadingProRata(false); }
    }, 500);
    return () => clearTimeout(t);
  }, [mode, form.loan_amount, headers]);

  const rpName = (code) => formOptions.risk_participants.find(r => r.code === code)?.name || '';

  const addManualRow = () => setManualRows([...manualRows, { risk_participant: '', amount: '', rp_rate: '' }]);
  const updateManualRow = (i, field, value) => {
    const rows = [...manualRows]; rows[i] = { ...rows[i], [field]: value }; setManualRows(rows);
  };
  const removeManualRow = (i) => setManualRows(manualRows.filter((_, idx) => idx !== i));
  const manualTotal = manualRows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  const totalAvailableCash = availableCash.reduce((sum, i) => sum + (i.available || 0), 0);

  const buildReview = async () => {
    if (!form.client_id || !form.interest_rate_pct || !form.repurchase_date) {
      toast.error('Please fill in Client, Interest Rate, and Repurchase Date');
      return;
    }
    const ltvPct = parseFloat(form.ltv_pct);
    if (!ltvPct || ltvPct <= 0 || ltvPct > 100) {
      toast.error('LTV must be greater than 0% and no more than 100%');
      return;
    }
    let allocations = [];
    if (form.deal_type === 'FEE') {
      // Stand-alone fee loan: booked to 05-00 at the full rate, VAT on both legs.
      if (!form.loan_amount || parseFloat(form.loan_amount) <= 0) {
        toast.error('Enter the fee amount (ex VAT) as the loan amount');
        return;
      }
      setReviewRows([{ risk_participant: FEE_RP, amount: parseFloat(form.loan_amount), name: 'Plane Tree Capital (fee)',
        resolved_rate: parseFloat(form.interest_rate_pct) / 100, rate_group: 'fee_tranche_full_rate' }]);
      setStep('review');
      return;
    } else if (mode === 'single') {
      if (!singleRp || !form.loan_amount) {
        toast.error('Please select a risk participant and enter the loan amount');
        return;
      }
      allocations = [{ risk_participant: singleRp, amount: parseFloat(form.loan_amount), rp_rate: singleRpRatePct ? parseFloat(singleRpRatePct) / 100 : null }];
    } else if (mode === 'pro_rata') {
      if (!form.loan_amount || proRataAllocations.length === 0) {
        toast.error('Enter a loan amount to compute the pro-rata split');
        return;
      }
      allocations = proRataAllocations.map(a => ({ risk_participant: a.risk_participant, amount: a.amount, rp_rate: null }));
    } else {
      const validRows = manualRows.filter(r => r.risk_participant && parseFloat(r.amount) > 0);
      if (validRows.length === 0) {
        toast.error('Add at least one investor allocation');
        return;
      }
      if (!form.loan_amount || Math.abs(manualTotal - parseFloat(form.loan_amount)) > 0.01) {
        toast.error(`Total allocated (${fmt(manualTotal)}) must equal the loan amount (${fmt(parseFloat(form.loan_amount) || 0)})`);
        return;
      }
      allocations = validRows.map(r => ({ risk_participant: r.risk_participant, amount: parseFloat(r.amount), rp_rate: r.rp_rate ? parseFloat(r.rp_rate) / 100 : null }));
    }

    setBuildingReview(true);
    try {
      const rows = await Promise.all(allocations.map(async (a) => {
        if (a.rp_rate != null) {
          return { ...a, name: rpName(a.risk_participant), resolved_rate: a.rp_rate, rate_group: 'manual' };
        }
        const r = await axios.get(`${API_URL}/api/lms/new-deals/rate-preview`, {
          headers, params: { risk_participant: a.risk_participant, interest_rate: parseFloat(form.interest_rate_pct) / 100 },
        });
        return { ...a, name: rpName(a.risk_participant), resolved_rate: r.data.rp_rate, rate_group: r.data.rate_group };
      }));
      setReviewRows(rows);
      setStep('review');
    } catch (err) {
      toast.error('Failed to compute investor rates for review');
    } finally {
      setBuildingReview(false);
    }
  };

  const totalAllocated = reviewRows.reduce((s, r) => s + r.amount, 0);
  const isFeeLoan = form.deal_type === 'FEE';
  const adminFeeAmount = isFeeLoan ? 0 : (parseFloat(form.admin_fee) || 0);
  const feeVatAmount = Math.round(adminFeeAmount * 0.15 * 100) / 100; // 15% VAT, matches backend ADMIN_FEE_VAT_RATE
  // A FEE loan carries VAT on its own amount (LB leg only), like a fee tranche.
  const feeLoanVat = isFeeLoan ? Math.round((parseFloat(form.loan_amount) || 0) * 0.15 * 100) / 100 : 0;

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let response;
      if (mode === 'single' || isFeeLoan) {  // a FEE loan is always one 05-00 tranche
        const a = reviewRows[0];
        response = await axios.post(`${API_URL}/api/lms/new-deals`, {
          client_id: form.client_id, risk_participant: a.risk_participant, loan_amount: a.amount,
          interest_rate: parseFloat(form.interest_rate_pct) / 100, admin_fee: adminFeeAmount,
          ltv: parseFloat(form.ltv_pct) / 100,
          rp_rate: a.rate_group === 'manual' ? a.resolved_rate : null,
          purchase_date: form.purchase_date, repurchase_date: form.repurchase_date,
          deal_type: form.deal_type, seller: form.seller, supplier: form.supplier, customer: form.customer,
          category: form.category, country_of_origin: form.country_of_origin, destination_country: form.destination_country,
          loan_number: form.loan_number ? parseInt(form.loan_number, 10) : null, include_api: isFeeLoan ? false : form.include_api,
          same_day_recycle: form.same_day_recycle,
        }, { headers });
        toast.success(`Deal recorded: ${response.data.loan_id}`);
      } else {
        response = await axios.post(`${API_URL}/api/lms/new-deals/multi`, {
          client_id: form.client_id,
          allocations: reviewRows.map(r => ({
            risk_participant: r.risk_participant, amount: r.amount,
            rp_rate: r.rate_group === 'manual' ? r.resolved_rate : null,
          })),
          interest_rate: parseFloat(form.interest_rate_pct) / 100, admin_fee: adminFeeAmount,
          ltv: parseFloat(form.ltv_pct) / 100,
          purchase_date: form.purchase_date, repurchase_date: form.repurchase_date,
          deal_type: form.deal_type, seller: form.seller, supplier: form.supplier, customer: form.customer,
          category: form.category, country_of_origin: form.country_of_origin, destination_country: form.destination_country,
          loan_number: form.loan_number ? parseInt(form.loan_number, 10) : null, include_api: form.include_api,
          same_day_recycle: form.same_day_recycle,
        }, { headers });
        toast.success(`Deal recorded: loan number ${response.data.loan_number} across ${response.data.tranches.length} tranche(s)`);
      }
      setLastResult(response.data);
      onCreated && onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record deal');
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => onOpenChange(false);
  const recordAnother = () => resetAll();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{lastResult ? 'Deal Recorded' : step === 'review' ? 'Review New Deal' : 'New Deal'}</DialogTitle>
          {!lastResult && (
            <DialogDescription>
              Originates a new loan tranche directly in the LoanBook / Risk Participation ledger. An admin fee, if
              entered, is booked once as its own internal tranche (plus 15% VAT), regardless of how many investors
              fund the deal — the fee is never drawn from any investor's account.
            </DialogDescription>
          )}
        </DialogHeader>

        {lastResult ? (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="font-medium">
                {mode === 'single' ? `Loan ${lastResult.loan_id} recorded` : `Loan number ${lastResult.loan_number} recorded across ${lastResult.tranches.length} tranche(s)`}
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Investor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead className="text-center">Transfer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(mode === 'single' ? [lastResult] : lastResult.tranches).map((t, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-xs">{t.risk_participant}</TableCell>
                    <TableCell className="text-right">{fmt(t.loan_amount)}</TableCell>
                    <TableCell className="text-right">{(t.rp_rate * 100).toFixed(2)}%</TableCell>
                    <TableCell className="text-center">
                      {!t.include_api ? <Badge variant="outline" className="text-xs">Ledger only</Badge>
                        : t.transfer_status === 'success' ? <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">Funded</Badge>
                        : t.transfer_status === 'partial' ? <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Partial — needs follow-up</Badge>
                        : t.transfer_status === 'skipped_no_mapping' ? <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">No account mapped</Badge>
                        : t.transfer_status === 'not_applicable' ? <Badge variant="outline" className="text-xs">N/A</Badge>
                        : <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs">Failed</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <DialogFooter>
              <Button variant="outline" onClick={recordAnother}>Record Another</Button>
              <Button onClick={close} className="bg-[#1D2D2A] hover:bg-[#243D37]">Done</Button>
            </DialogFooter>
          </div>
        ) : step === 'review' ? (
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs bg-gray-50 p-3 rounded-md">
              <div><span className="text-[#6C757D]">Client</span><p className="font-medium">{clients.find(c => c.client_id === form.client_id)?.company_name || form.client_id}</p></div>
              <div><span className="text-[#6C757D]">Interest Rate</span><p className="font-medium">{form.interest_rate_pct}%</p></div>
              <div><span className="text-[#6C757D]">Purchase → Repurchase</span><p className="font-medium">{form.purchase_date} → {form.repurchase_date}</p></div>
              <div><span className="text-[#6C757D]">Deal Type</span><p className="font-medium">{form.deal_type}</p></div>
              <div><span className="text-[#6C757D]">LTV</span><p className="font-medium">{form.ltv_pct}%</p></div>
              <div><span className="text-[#6C757D]">Gross Purchase Price</span><p className="font-medium">{fmt(totalAllocated / (parseFloat(form.ltv_pct) / 100))}</p></div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Risk Participant</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Basis</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reviewRows.map((r) => (
                  <TableRow key={r.risk_participant}>
                    <TableCell className="text-xs">{r.risk_participant} — {r.name}</TableCell>
                    <TableCell className="text-right">{fmt(r.amount)}{isFeeLoan && <span className="text-[#6C757D]"> + {fmt(feeLoanVat)} VAT</span>}</TableCell>
                    <TableCell className="text-right">{(r.resolved_rate * 100).toFixed(2)}%</TableCell>
                    <TableCell className="text-xs text-[#6C757D]">{r.rate_group.replace('_', ' ')}</TableCell>
                  </TableRow>
                ))}
                {adminFeeAmount > 0 && (
                  <TableRow className="bg-amber-50">
                    <TableCell className="text-xs">05-00 — Admin Fee (Plane Tree Capital)</TableCell>
                    <TableCell className="text-right">{fmt(adminFeeAmount)} <span className="text-[#6C757D]">+ {fmt(feeVatAmount)} VAT</span></TableCell>
                    <TableCell className="text-right">{form.interest_rate_pct}%</TableCell>
                    <TableCell className="text-xs text-[#6C757D]">fee tranche (fee accrues interest, VAT doesn't — not drawn from any investor)</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <div className="flex items-center justify-between text-sm bg-gray-50 p-2 rounded-md">
              <span className="text-[#6C757D]">Total to loan book</span>
              <span className="font-semibold">{fmt(totalAllocated + adminFeeAmount + feeVatAmount + feeLoanVat)}</span>
            </div>

            {form.same_day_recycle && (
              <div className="bg-purple-50 p-3 rounded-md text-xs text-purple-800">
                Same-Day Loan Recycle is ON — every tranche above is written to cashflow slot 1, so this drawdown
                won't accrue an extra day of interest on top of what the maturing facility already accrued through.
              </div>
            )}

            <div className="bg-blue-50 p-3 rounded-md text-xs text-blue-800">
              {isFeeLoan
                ? 'FEE loan: booked to 05-00 at the full rate with 15% VAT on both the LB and RP sides. No Investec transfer.'
                : form.include_api
                ? `Push to Investec is ON — each investor's amount above will be transferred from their call account into the fund's disbursement account.`
                : `Push to Investec is OFF — this will only write the ledger. No Investec transfer will be sent.`}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('form')}><ArrowLeft className="h-4 w-4 mr-2" />Back</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="submit-new-deal-btn">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Submitting...</> : 'Submit'}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="grid gap-4 py-2">
            <div className="flex items-center justify-between p-3 bg-slate-50 border rounded-lg">
              <div>
                <Label className="font-medium">Same-Day Loan Recycle</Label>
                <p className="text-xs text-[#6C757D]">
                  On: this deal replaces a facility maturing for the same client on this same day - the drawdown
                  won't accrue an extra day of interest on top of what the maturing facility already accrued through.
                  Leave off for a normal new deal.
                </p>
              </div>
              <Switch checked={form.same_day_recycle} onCheckedChange={(v) => setForm({ ...form, same_day_recycle: v })} data-testid="deal-same-day-recycle-toggle" />
            </div>
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => {
                const client = clients.find(c => c.client_id === v);
                // Auto-populate from this client's defaults - all stay
                // editable per-deal, since real data shows LTV genuinely
                // varies deal-to-deal even for the same client. Interest
                // rate depends on the client's rate_type: a flat fixed
                // default, or SA Prime + a per-client premium (moves
                // automatically whenever Prime is updated in Settings).
                let effectiveRatePct = form.interest_rate_pct;
                if (client?.rate_type === 'prime_linked' && client?.rate_premium != null && formOptions.sa_prime_rate != null) {
                  effectiveRatePct = String((formOptions.sa_prime_rate + client.rate_premium) * 100);
                } else if (client?.default_interest_rate != null) {
                  effectiveRatePct = String(client.default_interest_rate * 100);
                }
                setForm({
                  ...form, client_id: v, seller: form.seller || client?.company_name || v,
                  ltv_pct: client?.default_ltv != null ? String(client.default_ltv * 100) : form.ltv_pct,
                  interest_rate_pct: effectiveRatePct,
                  ...(() => {
                    const pct = client?.drawdown_fee_pct ? String(+(client.drawdown_fee_pct * 100).toFixed(4)) : form.admin_fee_pct;
                    return form.admin_fee_overridden
                      ? { admin_fee_pct: pct }
                      : { admin_fee_pct: pct, admin_fee: feeFromPct(pct, form.loan_amount) };
                  })(),
                });
              }}>
                <SelectTrigger data-testid="borrower-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map((client) => <SelectItem key={client.client_id} value={client.client_id}>{client.company_name || client.client_id}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>Total Loan Amount (ZAR) *</Label>
                <Input type="number" value={form.loan_amount} onChange={(e) => {
                  const newAmount = e.target.value;
                  setForm({ ...form, loan_amount: newAmount,
                    admin_fee: form.admin_fee_overridden ? form.admin_fee : feeFromPct(form.admin_fee_pct, newAmount) });
                }} placeholder="750000" data-testid="principal-input" />
              </div>
              <div>
                <Label>Interest Rate (%) *</Label>
                <Input type="number" step="0.01" value={form.interest_rate_pct} onChange={(e) => setForm({ ...form, interest_rate_pct: e.target.value })} placeholder="24" data-testid="deal-interest-rate-input" />
                {(() => {
                  const c = clients.find(cl => cl.client_id === form.client_id);
                  if (c?.rate_type === 'prime_linked' && c?.rate_premium != null && formOptions.sa_prime_rate != null) {
                    return <p className="text-xs text-[#6C757D] mt-1">Prime ({(formOptions.sa_prime_rate * 100).toFixed(2)}%) + premium ({(c.rate_premium * 100).toFixed(2)}%)</p>;
                  }
                  return null;
                })()}
              </div>
              <div>
                <Label>LTV (%) *</Label>
                <Input type="number" step="0.01" value={form.ltv_pct} onChange={(e) => setForm({ ...form, ltv_pct: e.target.value })} placeholder="100" data-testid="deal-ltv-input" />
              </div>
              {form.deal_type !== 'FEE' && (
              <div>
                <Label>Admin Fee (%)</Label>
                <Input type="number" step="0.01" value={form.admin_fee_pct} data-testid="deal-admin-fee-pct"
                  onChange={(e) => setForm({ ...form, admin_fee_pct: e.target.value, admin_fee_overridden: false,
                    admin_fee: feeFromPct(e.target.value, form.loan_amount) })} placeholder="0" />
                <Label className="text-xs mt-2 block">Fee amount (ZAR){form.admin_fee_overridden ? ' — overridden' : ''}</Label>
                <Input type="number" value={form.admin_fee} data-testid="deal-admin-fee-amount"
                  onChange={(e) => setForm({ ...form, admin_fee: e.target.value, admin_fee_overridden: true })} placeholder="0"
                  className={form.admin_fee_overridden ? 'border-amber-400' : ''} />
                <p className="text-xs text-[#6C757D] mt-1">
                  {form.admin_fee_overridden
                    ? <button type="button" className="underline" onClick={() => setForm({ ...form, admin_fee_overridden: false, admin_fee: feeFromPct(form.admin_fee_pct, form.loan_amount) })}>Reset to {form.admin_fee_pct || 0}% of the loan amount</button>
                    : `${form.admin_fee_pct || 0}% of the loan amount; type over the amount to override`}
                </p>
              </div>
              )}
            </div>

            {form.loan_amount && form.ltv_pct && parseFloat(form.ltv_pct) > 0 && (
              <div className="bg-slate-50 p-2 rounded-md text-xs text-[#6C757D]">
                Gross Purchase Price (loan amount ÷ LTV): <span className="font-medium text-[#212529]">{fmt(parseFloat(form.loan_amount) / (parseFloat(form.ltv_pct) / 100))}</span>
              </div>
            )}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Purchase (Drawdown) Date *</Label>
                <Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} />
              </div>
              <div>
                <Label>Repurchase Date *</Label>
                <Input type="date" value={form.repurchase_date} onChange={(e) => setForm({ ...form, repurchase_date: e.target.value })} data-testid="repurchase-date-input" />
              </div>
              <div>
                <Label>Existing Loan Number — optional</Label>
                <Input type="number" value={form.loan_number} onChange={(e) => setForm({ ...form, loan_number: e.target.value })} placeholder="leave blank for a new one" />
              </div>
            </div>

            {form.deal_type === 'FEE' && (
              <div className="border-t pt-4 text-sm bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="fee-loan-note">
                <strong>FEE loan.</strong> The loan amount is the fee (ex VAT). It's booked to 05-00 (Plane Tree Capital) at the full
                interest rate, with 15% VAT added on both the LB and RP sides (05-00 is treated as an investor), like the fee tranche of a normal loan. No investor
                allocation and no Investec transfer.
              </div>
            )}
            {form.deal_type !== 'FEE' && (<>
            {/* Allocation mode */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Investor Allocation</Label>
              <div className="flex gap-2 mt-2">
                {[['single', 'Single Investor'], ['pro_rata', 'Pro-Rata (all available)'], ['manual', 'Manual (multiple)']].map(([val, label]) => (
                  <Button key={val} type="button" size="sm" variant={mode === val ? 'default' : 'outline'}
                          className={mode === val ? 'bg-[#1D2D2A] hover:bg-[#243D37]' : ''}
                          onClick={() => setMode(val)} data-testid={`deal-mode-${val}`}>
                    {label}
                  </Button>
                ))}
              </div>

              {mode === 'single' && (
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div>
                    <Label>Risk Participant (Investor) *</Label>
                    <Select value={singleRp} onValueChange={setSingleRp}>
                      <SelectTrigger data-testid="deal-rp-select"><SelectValue placeholder="Select investor" /></SelectTrigger>
                      <SelectContent>
                        {formOptions.risk_participants.map((rp) => (
                          <SelectItem key={rp.code} value={rp.code}>{rp.code} — {rp.name} {rp.brand ? `(${rp.brand})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Investor Rate (%) — optional override</Label>
                    <Input type="number" step="0.01" value={singleRpRatePct} onChange={(e) => setSingleRpRatePct(e.target.value)} placeholder="auto-calculated" />
                  </div>
                  {!singleRpRatePct && (singleRp || loadingRatePreview) && (
                    <div className="col-span-2 bg-blue-50 p-2 rounded-md text-xs text-blue-800">
                      {loadingRatePreview ? 'Calculating investor rate…' : ratePreview ? (
                        <>Investor rate will be <span className="font-semibold">{(ratePreview.rp_rate * 100).toFixed(2)}%</span> ({ratePreview.rate_group.replace('_', ' ')})</>
                      ) : 'Enter an interest rate to preview the investor rate'}
                    </div>
                  )}
                </div>
              )}

              {mode === 'pro_rata' && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-[#6C757D]">
                    Splits the total loan amount proportionally across every investor's available Investec call-account
                    cash. Total available right now: <span className="font-medium">{fmt(totalAvailableCash)}</span>.
                  </p>
                  {loadingProRata ? (
                    <p className="text-xs text-[#6C757D]"><Loader2 className="h-3 w-3 inline animate-spin mr-1" />Computing split…</p>
                  ) : proRataAllocations.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                      <Table>
                        <TableBody>
                          {proRataAllocations.map((a) => (
                            <TableRow key={a.risk_participant}>
                              <TableCell className="text-xs py-1.5">{a.risk_participant} — {a.name}</TableCell>
                              <TableCell className="text-right text-xs py-1.5">{fmt(a.amount)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-xs text-amber-600">Enter a loan amount to compute the pro-rata split.</p>
                  )}
                </div>
              )}

              {mode === 'manual' && (
                <div className="mt-3 space-y-2">
                  {manualRows.map((row, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-6">
                        <Label className="text-xs">Investor</Label>
                        <Select value={row.risk_participant} onValueChange={(v) => updateManualRow(i, 'risk_participant', v)}>
                          <SelectTrigger><SelectValue placeholder="Select investor" /></SelectTrigger>
                          <SelectContent>
                            {formOptions.risk_participants.map((rp) => (
                              <SelectItem key={rp.code} value={rp.code}>
                                {rp.code} — {rp.name} {availableCash.find(a => a.risk_participant === rp.code) ? `(${fmt(availableCash.find(a => a.risk_participant === rp.code).available)} avail)` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3">
                        <Label className="text-xs">Amount (ZAR)</Label>
                        <Input type="number" value={row.amount} onChange={(e) => updateManualRow(i, 'amount', e.target.value)} placeholder="100000" />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Rate (%) override</Label>
                        <Input type="number" step="0.01" value={row.rp_rate} onChange={(e) => updateManualRow(i, 'rp_rate', e.target.value)} placeholder="auto" />
                      </div>
                      <div className="col-span-1">
                        <Button variant="ghost" size="sm" onClick={() => removeManualRow(i)} className="text-red-500 hover:text-red-700"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addManualRow} className="w-full"><Plus className="h-4 w-4 mr-2" />Add Investor</Button>
                  <div className="bg-gray-50 p-2 rounded text-sm">
                    <span className="text-[#6C757D]">Total Allocated: </span>
                    <span className={`font-medium ${Math.abs(manualTotal - (parseFloat(form.loan_amount) || 0)) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>{fmt(manualTotal)}</span>
                    <span className="text-[#6C757D]"> / {fmt(parseFloat(form.loan_amount) || 0)}</span>
                  </div>
                </div>
              )}
            </div>
            </>)}

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Deal Type</Label>
                <Select value={form.deal_type} onValueChange={(v) => setForm({ ...form, deal_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{formOptions.deal_types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category || '__none__'} onValueChange={(v) => setForm({ ...form, category: v === '__none__' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— none —</SelectItem>
                    {formOptions.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end pb-2">
                <div className="flex items-center gap-2">
                  <Switch checked={form.include_api} onCheckedChange={(v) => setForm({ ...form, include_api: v })} data-testid="deal-include-api-toggle" />
                  <Label className="text-sm">Push to Investec</Label>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Seller</Label>
                <Input list="deal-sellers-list" value={form.seller} onChange={(e) => setForm({ ...form, seller: e.target.value })} placeholder="Defaults to client name" />
                <datalist id="deal-sellers-list">{formOptions.sellers.map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <div>
                <Label>Supplier</Label>
                <Input list="deal-suppliers-list" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} />
                <datalist id="deal-suppliers-list">{formOptions.suppliers.map((s) => <option key={s} value={s} />)}</datalist>
              </div>
              <div>
                <Label>Customer</Label>
                <Input list="deal-customers-list" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} />
                <datalist id="deal-customers-list">{formOptions.customers.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Country of Origin</Label>
                <Select value={form.country_of_origin} onValueChange={(v) => setForm({ ...form, country_of_origin: v })}>
                  <SelectTrigger data-testid="deal-country-origin"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Destination Country</Label>
                <Select value={form.destination_country} onValueChange={(v) => setForm({ ...form, destination_country: v })}>
                  <SelectTrigger data-testid="deal-country-destination"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={buildReview} disabled={buildingReview} className="bg-[#1D2D2A] hover:bg-[#243D37]" data-testid="review-new-deal-btn">
                {buildingReview ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing review…</> : 'Review'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
